import { useStore } from '@src/stores-v2/StoreContext';
import {
  getUnifiedValue,
  modifyCompositionProperty,
} from '@src/utility/compositionSettings';
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';

import ColorDropdownSelect from '../../../common/ColorDropdownSelect';
import { ControlFieldLabel } from './ControlField';

import type { ColorControlConfiguration, ControlRenderProps } from '../types';

export const ColorControl = ({
  control,
  activeElement,
  modifyProperty,
  previewProperty,
  pickerTab,
  compositionMeta,
}: ControlRenderProps<ColorControlConfiguration>) => {
  const { editorStore, videoSourceStore } = useStore();
  const { unifiedValue } = getUnifiedValue(
    control,
    activeElement,
    compositionMeta,
  );
  const defaultValue =
    typeof unifiedValue === 'string' ? unifiedValue : undefined;

  // Holds the active hover-preview color, or null when not previewing. The
  // preview write round-trips back into `defaultValue` on surfaces that own
  // their editor (e.g. Pull Quotes); tracking it lets the effect ignore the echo.
  const previewColorRef = useRef<string | null>(null);

  const committedRef = useRef<string | undefined | null>(defaultValue);
  useEffect(() => {
    // Ignore our own preview echo; other external changes (e.g. undo while
    // hovering) still update the baseline. Assumes the color round-trips
    // unchanged — renderer format normalization would defeat this.
    if (defaultValue === previewColorRef.current) return;
    committedRef.current = defaultValue;
  }, [defaultValue]);

  // Refs let the debounced previewer stay stable across the re-renders that a
  // hover preview itself triggers (recreating it would cancel the pending
  // call) while still invoking the latest previewProperty/control.
  const previewPropertyRef = useRef(previewProperty);
  previewPropertyRef.current = previewProperty;
  const controlRef = useRef(control);
  controlRef.current = control;

  // Debounced canvas preview — avoids firing on every rapid hover (each global
  // update triggers a synchronous MobX chain that re-renders all observers).
  // Surfaces that own their renderer/editor (e.g. Pull Quotes) pass
  // `previewProperty` so the preview lands on THEIR canvas; otherwise we update
  // the global composition canvas directly. The revert path is NOT debounced.
  const debouncedPreview = useMemo(
    () =>
      debounce(
        (
          elementId: string,
          elementPaths: number[][],
          property: string,
          color: string,
        ) => {
          const preview = previewPropertyRef.current;
          if (preview) {
            preview(controlRef.current, color);
          } else {
            modifyCompositionProperty(
              elementId,
              editorStore,
              elementPaths,
              property,
              color,
              false,
            );
          }
        },
        50,
      ),
    [editorStore],
  );

  // Cancel any pending preview when this control unmounts.
  useEffect(() => () => debouncedPreview.cancel(), [debouncedPreview]);

  const handlePreview = (_propertyName: string, value: string | null) => {
    if (!control.elementPaths || !control.property) return;

    if (value === null) {
      // Revert: cancel any pending preview and restore the committed color
      // immediately on whichever canvas owns this control.
      previewColorRef.current = null;
      debouncedPreview.cancel();
      if (previewProperty) {
        previewProperty(control, committedRef.current ?? null);
      } else {
        modifyCompositionProperty(
          activeElement?.source.id ?? '',
          editorStore,
          control.elementPaths,
          control.property,
          committedRef.current ?? null,
          false,
        );
        // Flush the 300 ms VideoSourceStore debounce so the canvas snaps back instantly.
        videoSourceStore.flushChanges();
      }
    } else {
      previewColorRef.current = value;
      debouncedPreview(
        activeElement?.source.id ?? '',
        control.elementPaths,
        control.property,
        value,
      );
    }
  };

  return (
    <>
      <ControlFieldLabel>{control.label}</ControlFieldLabel>
      <ColorDropdownSelect
        action={(_p, value) => {
          // Drop any pending hover→click preview so it can't overwrite the commit.
          previewColorRef.current = null;
          debouncedPreview.cancel();
          committedRef.current = value ?? undefined;
          modifyProperty(control, value);
        }}
        previewAction={handlePreview}
        propertyName={control.property}
        defaultValue={defaultValue}
        pickerTab={pickerTab}
      />
    </>
  );
};
